import React, { useEffect, useMemo, useState } from "react";
import { Input, type InputAccent } from "./Input";

/**
 * Reusable datetime-local picker with sensible defaults.
 *
 * - `start` defaults to "now" — past times are blocked in the calendar.
 * - If a controlled `value` is already in the past (e.g. editing an expired
 *   record), the effective `min` widens to that value so the browser does not
 *   reject form submission.
 * - Returns both a parsed `Date` and the raw `YYYY-MM-DDTHH:mm` string via
 *   `onChanged` so callers can use whichever they prefer.
 */
export interface DateTimePickerChange {
  /** Parsed Date, or null when the field is empty / invalid. */
  date: Date | null;
  /** Raw `YYYY-MM-DDTHH:mm` string (also what `<input type="datetime-local">` uses). */
  value: string;
}

export interface DateTimePickerProps {
  /** Controlled value as `YYYY-MM-DDTHH:mm`. Omit for uncontrolled mode. */
  value?: string;
  /** Seed for uncontrolled mode. Ignored when `value` is provided. */
  initialValue?: Date | string;
  /** Earliest selectable instant. Default: now. */
  start?: Date | string;
  /** Latest selectable instant. Optional. */
  end?: Date | string;
  onChanged: (next: DateTimePickerChange) => void;
  required?: boolean;
  disabled?: boolean;
  id?: string;
  className?: string;
  /** Focus accent — mirrors `<Input accent>`. Default "blue". */
  accent?: InputAccent;
  /** Inline validation flag — forwarded to the underlying `<Input>`. */
  invalid?: boolean;
  "aria-label"?: string;
  "aria-labelledby"?: string;
}

function toDate(input: Date | string | undefined | null): Date | null {
  if (!input) return null;
  if (input instanceof Date)
    return Number.isNaN(input.getTime()) ? null : input;
  const d = new Date(input);
  return Number.isNaN(d.getTime()) ? null : d;
}

function toLocalValue(d: Date | null): string {
  if (!d) return "";
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(
    d.getDate(),
  )}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function nowLocalValue(): string {
  const d = new Date();
  d.setSeconds(0, 0);
  return toLocalValue(d);
}

export const DateTimePicker: React.FC<DateTimePickerProps> = ({
  value,
  initialValue,
  start,
  end,
  onChanged,
  required,
  disabled,
  id,
  className,
  accent,
  invalid,
  "aria-label": ariaLabel,
  "aria-labelledby": ariaLabelledBy,
}) => {
  const isControlled = value !== undefined;
  const [internal, setInternal] = useState<string>(() => {
    const seed = toDate(initialValue ?? null);
    return seed && seed.getTime() > 0 ? toLocalValue(seed) : "";
  });

  // Unset windows arrive as 0 / epoch — keep the field empty like create.
  const rawCurrent = isControlled ? (value as string) : internal;
  const current = (() => {
    if (!rawCurrent) return "";
    const ms = new Date(rawCurrent).getTime();
    if (!Number.isFinite(ms) || ms <= 0) return "";
    return rawCurrent;
  })();

  // Recompute "now" once a minute so the floor stays accurate if the picker
  // is mounted for a long time (e.g. across midnight).
  const [tick, setTick] = useState(0);
  useEffect(() => {
    const id = window.setInterval(() => setTick((t) => t + 1), 60_000);
    return () => window.clearInterval(id);
  }, []);

  const baseMin = useMemo(() => {
    const explicit = toDate(start);
    return explicit ? toLocalValue(explicit) : nowLocalValue();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [start, tick]);

  // If the current value is already below `start` (e.g. editing an expired
  // record), don't fight the existing value — widen min so the browser
  // doesn't flag a validation error on submit.
  const effectiveMin = useMemo(() => {
    if (!current) return baseMin;
    return current < baseMin ? current : baseMin;
  }, [baseMin, current]);

  const maxValue = useMemo(() => {
    const explicit = toDate(end);
    return explicit ? toLocalValue(explicit) : undefined;
  }, [end]);

  const handleChange = (raw: string) => {
    let next = raw;
    // Defensive clamp: keyboard entry can bypass the calendar's min/max.
    if (next && baseMin && next < baseMin) next = baseMin;
    if (next && maxValue && next > maxValue) next = maxValue;
    if (!isControlled) setInternal(next);
    const parsed = next ? new Date(next) : null;
    onChanged({
      date: parsed && !Number.isNaN(parsed.getTime()) ? parsed : null,
      value: next,
    });
  };

  return (
    <Input
      id={id}
      type="datetime-local"
      value={current}
      min={effectiveMin}
      max={maxValue}
      required={required}
      disabled={disabled}
      accent={accent}
      invalid={invalid}
      aria-label={ariaLabel}
      aria-labelledby={ariaLabelledBy}
      className={className}
      onChange={(e) => handleChange(e.target.value)}
    />
  );
};

export default DateTimePicker;
